// features/inventory/pages/BulkCountPage.tsx
/**
 * Bulk Count Upload Page — Enhanced with Preview, Detailed Results, and Clear Error Reporting.
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { BulkUploadProgress } from '@/components/bulk-upload/BulkUploadProgress';
import { BulkUploadErrors, BulkUploadErrorDetail } from '@/components/bulk-upload/BulkUploadErrors';
import {
    downloadCountTemplate,
    downloadCountTemplateAdvanced,
    bulkUploadCount,
    downloadBulkUploadErrorReport,
    BulkCountResult,
} from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft, Upload, Download, FileSpreadsheet, Store,
    CheckCircle, AlertTriangle, Info, Package, FileText,
    ArrowRight, RefreshCw, Eye, X, ChevronRight,
    Search, Filter, CheckCheck, XCircle, Minus,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

type WizardStep = 'template' | 'upload' | 'preview' | 'processing' | 'complete';

interface PreviewRow {
    row: number;
    productName: string;
    sku: string;
    barcode: string;
    countedQty: string;
    currentStock: string;
    matchMethod: string;
    status: 'valid' | 'warning' | 'error' | 'skipped';
    message?: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BulkCountPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [step, setStep] = useState<WizardStep>('template');
    const [branchId, setBranchId] = useState<string>(NIL_UUID);
    const [templateType, setTemplateType] = useState<'simple' | 'advanced'>('simple');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadResult, setUploadResult] = useState<BulkCountResult | null>(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
    const [previewSearch, setPreviewSearch] = useState('');
    const [previewFilter, setPreviewFilter] = useState<'all' | 'valid' | 'warning' | 'error' | 'skipped'>('all');

    const [bulkUploadState, setBulkUploadState] = useState<{
        isOpen: boolean; status: 'processing' | 'completed' | 'failed'; progress: number;
        steps: Array<{ id: string; label: string; icon: React.ElementType; status: 'pending' | 'active' | 'completed' | 'error' }>;
        result: BulkCountResult | null; errorMessage?: string;
    }>({
        isOpen: false, status: 'processing', progress: 0,
        steps: [
            { id: 'validate', label: 'Validating file format', icon: FileText, status: 'pending' },
            { id: 'match', label: 'Matching products', icon: Package, status: 'pending' },
            { id: 'update', label: 'Updating stock levels', icon: RefreshCw, status: 'pending' },
            { id: 'finalize', label: 'Finalizing', icon: CheckCircle, status: 'pending' },
        ],
        result: null, errorMessage: undefined,
    });

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-bulk-count', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const branchesList = (branches || []) as Array<{ id: string; name: string; code?: string }>;
    const isSingleStore = branchesList.length === 0;
    const getBranchName = (id: string) => branchesList.find((b: any) => b.id === id)?.name || 'Warehouse';

    // Mutations
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            setStep('processing');
            setBulkUploadState((prev) => ({
                ...prev, isOpen: true, status: 'processing', progress: 0,
                steps: prev.steps.map((s) => ({ ...s, status: 'pending' as const })),
                result: null, errorMessage: undefined,
            }));

            const updateProgress = (stepId: string, progress: number) => {
                setBulkUploadState((prev) => ({
                    ...prev, progress,
                    steps: prev.steps.map((s) => {
                        if (s.id === stepId) return { ...s, status: 'active' as const };
                        const si = prev.steps.findIndex((ps) => ps.id === s.id);
                        const ai = prev.steps.findIndex((ps) => ps.id === stepId);
                        if (si < ai) return { ...s, status: 'completed' as const };
                        return s;
                    }),
                }));
            };

            updateProgress('validate', 15);
            await new Promise((r) => setTimeout(r, 400));
            updateProgress('match', 40);

            const result = await bulkUploadCount(user!.tenantId, branchId || NIL_UUID, file);

            updateProgress('update', 75);
            await new Promise((r) => setTimeout(r, 300));
            updateProgress('finalize', 95);

            return result;
        },
        onSuccess: (result) => {
            const hasErrors = result.hasErrors || result.errorCount > 0;
            const allSkipped = result.skippedCount > 0 && result.successCount === 0 && result.errorCount === 0;
            setBulkUploadState((prev) => ({
                ...prev, progress: 100,
                status: hasErrors ? 'failed' : 'completed',
                steps: prev.steps.map((s) => ({
                    ...s,
                    status: s.id === 'finalize' && hasErrors ? ('error' as const) : ('completed' as const),
                })),
                result,
            }));
            setStep('complete');
            setUploadResult(result);

            queryClient.invalidateQueries({ queryKey: ['inventory'] });
            queryClient.invalidateQueries({ queryKey: ['inventory-counts'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            queryClient.invalidateQueries({ queryKey: ['low-stock-dash'] });

            if (allSkipped) {
                addToast({
                    type: 'warning',
                    title: 'No products updated',
                    message: 'All rows were skipped. Please fill in the "Counted Quantity" column in the Excel file.',
                });
            } else {
                addToast({
                    type: hasErrors ? 'warning' : 'success',
                    title: hasErrors ? 'Bulk count completed with issues' : 'Bulk count completed successfully',
                    message: `${result.successCount} updated, ${result.errorCount} errors, ${result.skippedCount} skipped`,
                });
            }
        },
        onError: (err: any) => {
            setStep('upload');
            setBulkUploadState((prev) => ({
                ...prev, progress: 100, status: 'failed',
                errorMessage: err?.message || 'An unexpected error occurred.',
                steps: prev.steps.map((s) => ({ ...s, status: s.status === 'active' ? 'error' as const : s.status })),
                result: null,
            }));
            addToast({ type: 'error', title: 'Upload failed', message: err?.message });
        },
    });

    // Simulated preview — reads the file name and shows a preview based on extension
    // In production, you'd call a backend preview endpoint
    const handleFileSelect = useCallback((file: File) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
            addToast({ type: 'error', title: 'Invalid file', message: 'Please upload an Excel (.xlsx, .xls) or CSV file.' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            addToast({ type: 'error', title: 'File too large', message: 'Maximum file size is 10MB.' });
            return;
        }
        setSelectedFile(file);
        setStep('preview');
    }, [addToast]);

    const handleDownloadTemplate = async (type: 'simple' | 'advanced') => {
        try {
            const fn = type === 'simple' ? downloadCountTemplate : downloadCountTemplateAdvanced;
            const blob = await fn(user!.tenantId, branchId || NIL_UUID);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `inventory-count-${type}-template.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            addToast({ type: 'success', title: 'Template downloaded' });
        } catch {
            addToast({ type: 'error', title: 'Download failed', message: 'Could not generate template.' });
        }
    };

    const handleDownloadErrorReport = async () => {
        if (!uploadResult?.uploadId) return;
        try {
            const blob = await downloadBulkUploadErrorReport(user!.tenantId, uploadResult.uploadId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `count-errors-${uploadResult.uploadId}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            addToast({ type: 'error', title: 'Download failed' });
        }
    };

    const resetForm = () => {
        setStep('template');
        setSelectedFile(null);
        setUploadResult(null);
        setShowErrorDetails(false);
        setPreviewRows([]);
        setPreviewSearch('');
        setPreviewFilter('all');
    };

    // Filtered preview rows
    const filteredPreviewRows = previewRows.filter(row => {
        if (previewSearch) {
            const q = previewSearch.toLowerCase();
            if (!row.productName.toLowerCase().includes(q) && !row.sku.toLowerCase().includes(q)) return false;
        }
        if (previewFilter !== 'all' && row.status !== previewFilter) return false;
        return true;
    });

    const previewStats = {
        total: previewRows.length,
        valid: previewRows.filter(r => r.status === 'valid').length,
        warning: previewRows.filter(r => r.status === 'warning').length,
        error: previewRows.filter(r => r.status === 'error').length,
        skipped: previewRows.filter(r => r.status === 'skipped').length,
    };

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/inventory/counts')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <Upload className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">Bulk Count Upload</h1>
                                <p className="text-sm text-slate-500">
                                    {step === 'template' && 'Step 1: Download template'}
                                    {step === 'preview' && 'Step 2: Review your file'}
                                    {step === 'processing' && 'Processing...'}
                                    {step === 'complete' && 'Complete'}
                                </p>
                            </div>
                        </div>
                        {/* Step Indicator */}
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4].map((s) => (
                                <div key={s} className="flex items-center gap-1">
                                    <div className={clsx(
                                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                                        (step === 'template' && s === 1) ||
                                            (step === 'preview' && s <= 2) ||
                                            (step === 'processing' && s <= 3) ||
                                            (step === 'complete' && s <= 4)
                                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                                            : 'bg-slate-200 text-slate-500'
                                    )}>
                                        {s < 4 && step !== 'template' && (s === 1 || s === 2 || (step === 'processing' && s === 3) || step === 'complete') ? (
                                            <CheckCircle className="w-4 h-4" />
                                        ) : s}
                                    </div>
                                    {s < 4 && <div className={clsx('w-6 h-0.5 rounded', step !== 'template' && s < (step === 'preview' ? 2 : step === 'processing' ? 3 : 4) ? 'bg-emerald-400' : 'bg-slate-200')} />}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 space-y-6">
                {/* STEP 1: Template Download */}
                {step === 'template' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        {/* Branch Selection */}
                        {!isSingleStore && branchesList.length > 0 && (
                            <Card>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                            <Store className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">Select Branch</h3>
                                            <p className="text-xs text-slate-500">Which branch are you counting?</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {branchesList.map((b: any) => (
                                            <button key={b.id} onClick={() => setBranchId(b.id)}
                                                className={clsx('p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                                                    branchId === b.id ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20' : 'border-slate-200 hover:border-emerald-300')}
                                                type="button">
                                                <Store className={clsx('w-5 h-5 mb-2', branchId === b.id ? 'text-emerald-600' : 'text-slate-400')} />
                                                <p className="font-semibold text-sm">{b.name}</p>
                                                {b.code && <p className="text-xs text-slate-400">{b.code}</p>}
                                            </button>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Template Selection */}
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                        <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">Choose Template</h3>
                                        <p className="text-xs text-slate-500">Download and fill with counted quantities</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button onClick={() => setTemplateType('simple')}
                                        className={clsx('p-5 rounded-xl border-2 text-left transition-all',
                                            templateType === 'simple' ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20' : 'border-slate-200 hover:border-emerald-300')}
                                        type="button">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={clsx('p-2 rounded-lg', templateType === 'simple' ? 'bg-emerald-100' : 'bg-slate-100')}>
                                                <FileText className={clsx('w-5 h-5', templateType === 'simple' ? 'text-emerald-600' : 'text-slate-400')} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">Simple Template</h4>
                                                <p className="text-xs text-slate-400">Basic product info + quantity</p>
                                            </div>
                                        </div>
                                        <ul className="text-xs text-slate-500 space-y-1 pl-2">
                                            <li>• Product Name, SKU, Barcode</li>
                                            <li>• Counted Quantity column</li>
                                            <li>• Notes column</li>
                                        </ul>
                                    </button>
                                    <button onClick={() => setTemplateType('advanced')}
                                        className={clsx('p-5 rounded-xl border-2 text-left transition-all',
                                            templateType === 'advanced' ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20' : 'border-slate-200 hover:border-emerald-300')}
                                        type="button">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={clsx('p-2 rounded-lg', templateType === 'advanced' ? 'bg-emerald-100' : 'bg-slate-100')}>
                                                <FileSpreadsheet className={clsx('w-5 h-5', templateType === 'advanced' ? 'text-emerald-600' : 'text-slate-400')} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">Advanced Template</h4>
                                                <p className="text-xs text-slate-400">Batch numbers, expiry dates</p>
                                            </div>
                                        </div>
                                        <ul className="text-xs text-slate-500 space-y-1 pl-2">
                                            <li>• All simple template fields</li>
                                            <li>• Batch Number column</li>
                                            <li>• Expiry Date column</li>
                                            <li>• Variant support</li>
                                        </ul>
                                    </button>
                                </div>

                                <div className="mt-6 flex items-center gap-3">
                                    <button onClick={() => handleDownloadTemplate(templateType)}
                                        className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25"
                                        type="button">
                                        <Download className="w-4 h-4" /> Download {templateType === 'simple' ? 'Simple' : 'Advanced'} Template
                                    </button>
                                    <button onClick={() => setStep('upload')}
                                        className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-emerald-200 text-emerald-700 rounded-xl hover:bg-emerald-50 transition-all font-semibold text-sm"
                                        type="button">
                                        I have my file ready <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-blue-700">How it works</p>
                                        <ul className="text-xs text-blue-600 mt-1 space-y-1">
                                            <li>• Template comes pre-filled with all your products</li>
                                            <li>• Fill in the <strong>Counted Quantity</strong> column with physical counts</li>
                                            <li>• Leave blank for products you didn't count</li>
                                            <li>• Products are matched by barcode, SKU, or name automatically</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 2: Upload File */}
                {step === 'upload' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                        <Upload className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">Upload Your Filled Template</h3>
                                        <p className="text-xs text-slate-500">Branch: {getBranchName(branchId)} · Template: {templateType}</p>
                                    </div>
                                </div>

                                <div
                                    onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFileSelect(f); }}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    className={clsx('border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer',
                                        isDragging ? 'border-emerald-400 bg-emerald-50/50 scale-[1.02]' : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50')}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { handleFileSelect(f); e.target.value = ''; } }} />
                                    <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                        <Upload className="w-8 h-8 text-emerald-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-700">
                                        {isDragging ? 'Drop your file here' : 'Click or drag file to upload'}
                                    </h3>
                                    <p className="text-sm text-slate-400 mt-1">Excel (.xlsx, .xls) or CSV · Max 10MB</p>
                                </div>

                                <div className="mt-4 flex justify-center">
                                    <button onClick={() => setStep('template')}
                                        className="text-sm text-slate-400 hover:text-slate-600">
                                        ← Back to Template
                                    </button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 3: Preview */}
                {step === 'preview' && selectedFile && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                        <Eye className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">Preview Your Upload</h3>
                                        <p className="text-xs text-slate-500">
                                            {selectedFile.name} · Branch: {getBranchName(branchId)}
                                        </p>
                                    </div>
                                </div>

                                {/* Warning if no counted quantities */}
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4 flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-amber-700">Important</p>
                                        <p className="text-xs text-amber-600 mt-0.5">
                                            Make sure the <strong>"Counted Quantity"</strong> column has values.
                                            Empty rows will be skipped during processing.
                                        </p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3">
                                    <button onClick={() => uploadMutation.mutate(selectedFile)}
                                        disabled={uploadMutation.isPending}
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm disabled:opacity-50"
                                        type="button">
                                        {uploadMutation.isPending ? <Spinner size="xs" variant="white" /> : <Upload className="w-4 h-4" />}
                                        Process Upload
                                    </button>
                                    <button onClick={() => { setSelectedFile(null); setStep('upload'); }}
                                        className="px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                                        type="button">
                                        Change File
                                    </button>
                                    <button onClick={() => setStep('template')}
                                        className="text-sm text-slate-400 hover:text-slate-600">
                                        Cancel
                                    </button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 4: Complete — Detailed Results */}
                {step === 'complete' && uploadResult && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        {/* All Skipped Warning */}
                        {uploadResult.skippedCount > 0 && uploadResult.successCount === 0 && uploadResult.errorCount === 0 && (
                            <div className="p-5 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-start gap-3">
                                <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h3 className="font-bold text-amber-800">No Products Were Updated</h3>
                                    <p className="text-sm text-amber-700 mt-1">
                                        All {uploadResult.totalRows} rows were skipped because the
                                        <strong> "Counted Quantity"</strong> column is empty.
                                    </p>
                                    <p className="text-xs text-amber-600 mt-2">
                                        Please fill in the counted quantities in your Excel file and upload again.
                                    </p>
                                    <button onClick={() => setStep('template')}
                                        className="mt-3 text-sm font-medium text-amber-700 underline hover:text-amber-800">
                                        Download Template Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Result Summary */}
                        <Card className={clsx('border-2',
                            uploadResult.hasErrors ? 'border-amber-200' :
                                uploadResult.successCount === 0 ? 'border-amber-200' : 'border-emerald-200')}>
                            <CardContent className="p-6 text-center">
                                <div className={clsx('w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4',
                                    uploadResult.hasErrors || uploadResult.successCount === 0 ? 'bg-amber-100' : 'bg-emerald-100')}>
                                    {uploadResult.hasErrors || uploadResult.successCount === 0 ? (
                                        <AlertTriangle className="w-8 h-8 text-amber-600" />
                                    ) : (
                                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                                    )}
                                </div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    {uploadResult.successCount === 0 && uploadResult.errorCount === 0
                                        ? 'Nothing Was Updated'
                                        : uploadResult.hasErrors
                                            ? 'Completed with Issues'
                                            : 'Bulk Count Successful!'}
                                </h2>
                                <p className="text-sm text-slate-500 mt-2">
                                    {uploadResult.successCount} updated · {uploadResult.errorCount} errors · {uploadResult.skippedCount} skipped
                                </p>
                            </CardContent>
                        </Card>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total Rows</p>
                                <p className="text-2xl font-bold text-slate-900">{uploadResult.totalRows}</p>
                            </div>
                            <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-center">
                                <p className="text-[10px] text-emerald-600 uppercase tracking-wider">✓ Updated</p>
                                <p className="text-2xl font-bold text-emerald-700">{uploadResult.successCount}</p>
                            </div>
                            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 text-center">
                                <p className="text-[10px] text-amber-600 uppercase tracking-wider">⊘ Skipped</p>
                                <p className="text-2xl font-bold text-amber-700">{uploadResult.skippedCount || 0}</p>
                            </div>
                            <div className="bg-rose-50 rounded-2xl border border-rose-200 p-4 text-center">
                                <p className="text-[10px] text-rose-600 uppercase tracking-wider">✗ Errors</p>
                                <p className="text-2xl font-bold text-rose-700">{uploadResult.errorCount}</p>
                            </div>
                        </div>

                        {/* Error Details */}
                        {uploadResult.hasErrors && uploadResult.errors.length > 0 && (
                            <Card className="border-2 border-rose-200">
                                <CardHeader gradient>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            <XCircle size={18} className="text-rose-500" />
                                            Failed Products ({uploadResult.errors.length})
                                        </h3>
                                        <button onClick={() => setShowErrorDetails(true)}
                                            className="text-xs font-medium text-rose-600 hover:text-rose-700">
                                            View All Errors
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {uploadResult.errors.slice(0, 10).map((error, i) => (
                                            <div key={i} className="flex items-start gap-3 p-3 bg-rose-50 rounded-xl">
                                                <XCircle size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-slate-700">
                                                        Row {error.row}: {error.productName}
                                                    </p>
                                                    <p className="text-xs text-rose-600 mt-0.5">{error.message}</p>
                                                    {error.suggestedFix && (
                                                        <p className="text-xs text-slate-500 mt-0.5">Fix: {error.suggestedFix}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {uploadResult.errors.length > 10 && (
                                            <p className="text-xs text-slate-400 text-center py-2">
                                                +{uploadResult.errors.length - 10} more errors. View full details.
                                            </p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Actions */}
                        <div className="flex justify-center gap-3 flex-wrap">
                            {uploadResult.hasErrors && (
                                <>
                                    <button onClick={() => setShowErrorDetails(true)}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors text-sm font-semibold"
                                        type="button">
                                        <AlertTriangle className="w-4 h-4" /> View Error Details ({uploadResult.errorCount})
                                    </button>
                                    <button onClick={handleDownloadErrorReport}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-white border border-rose-200 text-rose-700 rounded-xl hover:bg-rose-50 transition-colors text-sm font-semibold"
                                        type="button">
                                        <Download className="w-4 h-4" /> Download Error Report
                                    </button>
                                </>
                            )}
                            <button onClick={resetForm}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button">
                                <Upload className="w-4 h-4" /> Upload Another
                            </button>
                            <button onClick={() => navigate('/inventory/counts')}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm"
                                type="button">
                                Go to Counts
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Progress Modal */}
            <BulkUploadProgress
                isOpen={bulkUploadState.isOpen && !showErrorDetails}
                onClose={() => {
                    if (bulkUploadState.status !== 'processing') {
                        setBulkUploadState(prev => ({ ...prev, isOpen: false }));
                        if (bulkUploadState.result?.hasErrors) setShowErrorDetails(true);
                    }
                }}
                steps={bulkUploadState.steps}
                progress={bulkUploadState.progress}
                status={bulkUploadState.status}
                stats={{
                    totalRows: bulkUploadState.result?.totalRows || 0,
                    successCount: bulkUploadState.result?.successCount || 0,
                    errorCount: bulkUploadState.result?.errorCount || 0,
                    categoriesCreated: 0,
                }}
                errorMessage={bulkUploadState.errorMessage}
                onDownloadErrors={uploadResult?.hasErrors ? handleDownloadErrorReport : undefined}
            />

            {/* Error Details Modal */}
            <BulkUploadErrors
                isOpen={showErrorDetails}
                onClose={() => setShowErrorDetails(false)}
                errors={(uploadResult?.errors || []) as BulkUploadErrorDetail[]}
                totalRows={uploadResult?.totalRows || 0}
                successCount={uploadResult?.successCount || 0}
                errorCount={uploadResult?.errorCount || 0}
                onDownloadReport={uploadResult?.hasErrors ? handleDownloadErrorReport : undefined}
            />
        </div>
    );
}